import React, { useCallback, useRef, useState } from 'react';
import { message, Table, TablePaginationConfig } from 'antd';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { BenefitStatus, BenefitType } from '../../../types/benefit';
import { BENEFIT } from '../../../constants/define/translation';
import {
  ActionsColumn,
  LinkColumn,
  IndexColumn,
  ShowHideColumn,
  StatusColumn,
  TitleColumn,
  PartnerColumn,
} from './columns';
import { appStyles } from '../../../styles';
import { postSortBenefitsRepository } from '../../../repository/mytm/benefit/benefitRepository';
import { MESSAGE_ERROR } from '../../../constants/message';

interface BenefitTableProps {
  records: BenefitType[];
  onRefresh: () => void;
  setIsOpenModal: Function;
  setDataRecord: Function;
  handleChangePageActive: Function;
  loading?: boolean;
  className?: string;
  isDraft?: boolean;
  pagination?: TablePaginationConfig;
}

const DraggableBodyRow = ({ index, moveRow, style, ...restProps }: any) => {
  const ref = useRef();
  const type = 'DraggableBodyRow';

  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor: any) => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName: dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
      };
    },
    drop: (item: any) => {
      moveRow(item.index, index);
    },
  });
  const [, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drop(drag(ref));
  return (
    <tr ref={ref} className={`${isOver ? dropClassName : ''}`} style={{ cursor: 'move', ...style }} {...restProps} />
  );
};

const { Column } = Table;

export const BenefitTable = (props: BenefitTableProps) => {
  const { t } = useTranslation(BENEFIT);
  const [isLoading, setIsLoading] = useState(false);
  const { pagination, records, loading, className, onRefresh } = props;
  const components: any = {
    body: {
      row: DraggableBodyRow,
    },
  };

  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      setIsLoading(true);
      const dragRecordsList = [...records];
      const dragData = dragRecordsList[dragIndex];
      dragRecordsList.splice(dragIndex, 1);
      dragRecordsList.splice(hoverIndex, 0, dragData);
      const recordSorts = dragRecordsList.map((record, index) => {
        return {
          id: record.id,
          sortOrder: index + 1,
        };
      });

      postSortBenefitsRepository({
        sortBenefitList: recordSorts,
      })
        .then(() => {
          setIsLoading(false);
          onRefresh();
        })
        .catch(() => {
          message.error(MESSAGE_ERROR.M02);
          setIsLoading(false);
        });
    },
    [records, onRefresh],
  );

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <StyledTable
          dataSource={records}
          loading={loading || isLoading}
          className={className}
          rowKey="index"
          pagination={pagination}
          components={components}
          summary={() => {
            return (
              <>
                <Table.Summary.Row>
                  <Table.Summary.Cell colSpan={2} index={0}>
                    Total: {pagination?.total}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </>
            );
          }}
          onRow={(recordDeep: any, index?: number): any => {
            return {
              index,
              moveRow,
            };
          }}
        >
          <Column
            title={t('table.title.index')}
            align="center"
            width={32}
            dataIndex="index"
            key="index"
            render={value => <IndexColumn value={value} />}
          />
          <Column
            title={t('table.title.show')}
            align="center"
            width={100}
            dataIndex="show"
            key="show"
            render={(_, record: BenefitType) => <ShowHideColumn onRefresh={onRefresh} record={record} />}
          />
          <Column
            title={t('table.title.title')}
            align="center"
            width={80}
            dataIndex="title"
            key="title"
            render={(_, record: BenefitType) => <TitleColumn record={record} />}
          />
          <Column
            title={t('table.title.link')}
            align="center"
            width={80}
            dataIndex="title"
            key="title"
            render={(_, record: BenefitType) => <LinkColumn record={record} />}
          />
          <Column
            title={t('table.title.partner')}
            align="center"
            width={100}
            dataIndex="title"
            key="title"
            render={(_, record: BenefitType) => <PartnerColumn record={record} />}
          />
          <Column
            title={t('table.title.status')}
            align="center"
            width={62}
            dataIndex="status"
            key="status"
            render={(_, record: BenefitType) => (
              <StatusColumn
                status={record.disabled ? BenefitStatus.INACTIVE : BenefitStatus.ACTIVE}
                isActive={!record.disabled}
                width="120px"
              />
            )}
          />
          <Column
            title={t('table.title.action')}
            align="left"
            key="action"
            width={100}
            render={(_, record: BenefitType) => <ActionsColumn total={records.length} record={record} {...props} />}
          />
        </StyledTable>
      </DndProvider>
    </>
  );
};

const StyledTable = styled(Table)`
  .ant-table-thead {
    border-bottom: 2px solid #eee;
    & > th > tr {
      ${appStyles.background.tableBgGray}
      text-transform: uppercase;
      font-size: 0.75rem;
      font-weight: 600;
      border: 1px solid #dededee3;
      &:before {
        display: none;
      }
    }
  }

  .card-name-tiles {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-height: 21px;
    max-height: 48px;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  & th.action-home {
    text-align: center !important;
  }
`;
